/* eslint-disable prefer-arrow-callback */
import React, { useEffect, useMemo } from 'react';
import { findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {
  Button,
  CardActions,
  CardContent,
  Grid,
  List,
  ListItem,
  Paper,
  TextField,
  Typography,
} from '@availity/element';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useAppContext } from '@/context';
import { useFRCallback } from '@/hooks';

const NullEmail = ({ ...props }) => {
  const { stepToRender = {}, setCurrentStep, errorCode } = props;
  const { invalidNewEmails, addInvalidNewEmail } = useAppContext();
  const {
    callbacks = [],
    choiceCallbackChoices,
    choiceCallbackIndex,
    stringAttributeInputCallbackIndex,
  } = useFRCallback(stepToRender);

  // initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const emailFormValue = watch('email');

  // if a 109 error code is present, add the current email to the invalid emails array in context
  useEffect(() => {
    if (errorCode) {
      addInvalidNewEmail(emailFormValue);
      setError('email', 'The email address you entered is not valid. Please try again.');
    }
  }, [errorCode]);

  // NOTE: if coming from a null email scenario, there shouldn't be a back navigation option
  const hasBackButton = useMemo(() => {
    let output = false;
    if (choiceCallbackChoices?.length > 0) {
      for (const button of choiceCallbackChoices) {
        const lowercaseButton = button?.toLowerCase();
        output = ['back'].indexOf(lowercaseButton) >= 0;
      }
    }

    return output;
  }, [choiceCallbackChoices]);

  // for handling navigation outside of form submit
  const handleNavigation = (selectedNavigation) => {
    const nextStep = { ...stepToRender };

    // get the index of the selected navigation option
    const selectedNavigationIndex = findIndex(
      choiceCallbackChoices,
      (button) => button?.toLowerCase() === selectedNavigation?.toLowerCase()
    );

    const newCallbacks = [...callbacks];

    if (selectedNavigationIndex >= 0) {
      // set the value of the navigation in the input of the confirmation callback
      newCallbacks[choiceCallbackIndex].input[0] = {
        ...newCallbacks[choiceCallbackIndex].input[0],
        value: selectedNavigationIndex,
      };
    }

    // overwrite the old callbacks with the newly constructed callbacks
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // set the current step to trigger the navigation
    setCurrentStep({ ...nextStep });
  };

  // handle form submission with email; uses default navigation.
  const onSubmit = (values) => {
    const { email = '' } = values;

    const nextStep = { ...stepToRender };

    const newCallbacks = [...callbacks];
    if (stringAttributeInputCallbackIndex >= 0) {
      newCallbacks[stringAttributeInputCallbackIndex].input[0] = {
        ...newCallbacks[stringAttributeInputCallbackIndex].input[0],
        value: email,
      };
    }

    // overwrite the old callbacks with the newly constructed callbacks
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // set the current step to trigger the navigation
    setCurrentStep({ ...nextStep });
  };

  return (
    <Grid
      id="null-email-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '600px' } }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Typography variant="h1" mb={2}>
                  Enter your email address for login help
                </Typography>
                <List
                  component="ul"
                  sx={{
                    listStyleType: 'disc',
                    listStylePosition: 'outside',
                    paddingLeft: 3,
                    marginBottom: 4,
                  }}
                  disablePadding
                >
                  <ListItem disableGutters disablePadding divider={false} sx={{ display: 'list-item' }}>
                    <Typography variant="subtitle1">
                      Use an email address unique to you (not shared with others).
                    </Typography>
                  </ListItem>
                  <ListItem disableGutters disablePadding divider={false} sx={{ display: 'list-item' }}>
                    <Typography variant="subtitle1">
                      Quickly and easily reset your password or retrieve your user ID.
                    </Typography>
                  </ListItem>
                  <ListItem disableGutters disablePadding divider={false} sx={{ display: 'list-item' }}>
                    <Typography variant="subtitle1">
                      We won&apos;t give your email address to others. We hate spam, too.
                    </Typography>
                  </ListItem>
                </List>
                <TextField
                  id="email"
                  label="My email address is:"
                  margin="normal"
                  display="flex"
                  {...register('email', {
                    maxLength: {
                      value: 60,
                      message: 'The email address you entered is not in a valid format. Please try again.',
                    },
                    required: 'The email address you entered is not in a valid format. Please try again.',
                    pattern: {
                      value:
                        /^[\w!#$&'+/-]+(?:\.[\w!#$&'+/-]+)*@(?:[\da-z](?:[\da-z-]*[\da-z])?\.)+[\da-z](?:[\da-z-]*[\da-z])?$/i,
                      message: 'The email address you entered is not in a valid format. Please try again.',
                    },
                    validate: {
                      emailIsNotInInvalidArray: (value) => {
                        let isValid = true;
                        if (value) {
                          const emailFound = invalidNewEmails.indexOf(value) >= 0;
                          if (emailFound) {
                            isValid = false;
                          }
                        }

                        return isValid || 'The email address you entered is not valid. Please try again.';
                      },
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </CardContent>
              <CardActions sx={{ justifyContent: 'end' }}>
                {hasBackButton && (
                  <Button color="secondary" onClick={() => handleNavigation('back')}>
                    Back
                  </Button>
                )}
                <Button color="primary" type="submit">
                  Submit
                </Button>
              </CardActions>
            </form>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
};

NullEmail.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
};

export default NullEmail;
